import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SEO } from "../components/SEO";

const LoginPage: React.FC = () => {
  const { login, isAuthenticated, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");

  // ログイン済みの場合はダッシュボードにリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // URLパラメータからエラーを検出
  useEffect(() => {
    const error = searchParams.get("error");

    if (error) {
      const errorMessages: Record<string, string> = {
        auth_failed: "認証に失敗しました。再度お試しください。",
        state_mismatch:
          "セキュリティエラーが発生しました。再度お試しください。",
        no_code: "認証コードが取得できませんでした。",
        token_failed: "トークンの取得に失敗しました。",
        userinfo_failed: "ユーザー情報の取得に失敗しました。",
        email_exists: "このメールアドレスは既に別の方法で登録されています。",
        user_creation_failed: "ユーザーの作成に失敗しました。",
        server_error:
          "サーバーエラーが発生しました。しばらくしてから再度お試しください。",
      };

      setErrorMessage(errorMessages[error] || "不明なエラーが発生しました。");

      // URLからエラーパラメータを除去
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [searchParams]);

  const handleGoogleLogin = () => {
    clearError();
    setErrorMessage("");
    login();
  };

  const handleCloseError = () => {
    setErrorMessage("");
  };

  return (
    <>
      <SEO
        title="ログイン"
        description="Googleアカウントでログインして、就活を効率的に管理しましょう。企業情報、選考状況、面接スケジュールを一元管理できる無料ツールです。"
        canonical="/login"
        keywords={["ログイン", "Google認証", "就活ツール", "OAuth"]}
        openGraph={{
          type: "website",
          title: "ログイン | 就活ダッシュボード",
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "ログイン",
          description: "Googleアカウントでログインして就活管理を開始",
        }}
      />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 p-5">
        <div className="bg-white rounded-xl shadow-2xl p-10 w-full max-w-[420px] text-center">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-br from-indigo-500 to-purple-600 m-0 mb-2">
              就活ダッシュボード
            </h1>
            <p className="text-gray-600 text-base m-0 mb-4">
              就活企業とスケジュールを管理
            </p>
            <div className="text-left bg-gray-50 rounded-lg p-4 mt-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">
                このアプリについて
              </h2>
              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li>企業情報の一元管理</li>
                <li>選考状況の進捗トラッキング</li>
                <li>面接スケジュールのカレンダー管理</li>
                <li>Google Calendarとの同期機能</li>
                <li>エントリーシートのテンプレート管理</li>
              </ul>
              <p className="text-xs text-gray-500 mt-3">
                日本の大学生の就職活動を効率化するための無料ツールです。
              </p>
            </div>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg py-3 px-4 mb-5 flex items-center justify-between text-red-700 text-sm">
              <span>{errorMessage}</span>
              <button
                className="bg-transparent border-none text-red-700 text-xl cursor-pointer p-0 ml-2 leading-none hover:text-red-800"
                onClick={handleCloseError}
                aria-label="エラーを閉じる"
              >
                ×
              </button>
            </div>
          )}

          <div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Googleアカウントでログインして、就活を効率的に管理しましょう。
            </p>

            <div className="mb-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-gray-700 mb-2">
                このアプリは以下の情報にアクセスします：
              </p>
              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li>Googleアカウント情報（名前、メールアドレス）</li>
                <li>
                  Google Calendarのイベント（同期機能を有効にした場合のみ）
                </li>
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                詳細は
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 font-semibold underline hover:text-blue-700 mx-1"
                >
                  プライバシーポリシー
                </a>
                をご確認ください。
              </p>
            </div>

            <button
              onClick={handleGoogleLogin}
              className="flex items-center justify-center gap-3 w-full py-3.5 px-6 border-2 border-gray-300 rounded-lg bg-white text-gray-800 text-base font-medium cursor-pointer transition-all hover:border-gray-400 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-md focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
            >
              <GoogleIcon />
              <span>Googleアカウントでログイン</span>
            </button>
          </div>

          <div className="mt-8 pt-5 border-t border-gray-200">
            <p className="text-xs text-gray-400 leading-snug m-0">
              ログインすることで、
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-500 no-underline hover:underline"
              >
                利用規約
              </a>
              および
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-500 no-underline hover:underline"
              >
                プライバシーポリシー
              </a>
              に同意したものとみなされます。
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

// Google アイコンコンポーネント
const GoogleIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18Z"
    />
    <path
      fill="#34A853"
      d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.01a4.8 4.8 0 0 1-2.7.75c-2.08 0-3.84-1.4-4.48-3.29H1.83v2.07A8 8 0 0 0 8.98 17Z"
    />
    <path
      fill="#FBBC05"
      d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07Z"
    />
    <path
      fill="#EA4335"
      d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.3Z"
    />
  </svg>
);

export default LoginPage;
